<template lang="pug">
.custom-public-setting-table
  b-table(:items="_value" :fields="fields"
    :busy="isLoading"
    no-border-collapse table-class="basic-table" show-empty)
    //- ローディング
    template(#table-busy)
      .text-center.table-loading-message
        b-spinner.spinner
        span.message.align-self-center 読込み中...
    //- レコード無し
    template(#empty)
      .empty-message
        span 情報がありません

    //- セル：用途
    template(#cell(useCase)="row")
      span {{ convertUseCaseName(row.item) }}
    //- セル：部屋種別
    template(#cell(roomType)="row")
      span {{ convertRoomTypeName(row.item) }}
    //- セル：空床情報
    template(#cell(receptionStatus)="row")
      b-form-select(v-model="row.item.receptionStatus" :options="receptionStatusOptions"
        :class="{ 'disable': disableReceptionStatus(row.item) }" :disabled="disableReceptionStatus(row.item)")
    //- セル：空床数
    template(#cell(receptionCount)="row")
      b-form-select(v-model="row.item.receptionCount" :options="receptionCountOptions"
        :class="{ 'disable': disableReceptionCount(row.item) }" :disabled="disableReceptionCount(row.item)")
    //- セル：使用有無
    template(#cell(use)="row")
      EnableSwitch(v-model="row.item.use")
</template>

<script lang="ts">
import { Component, Inject, Prop, Vue } from "vue-property-decorator";
import { Validator } from "vee-validate";

import { CustomPublicSetting, ReceptionCount, ReceptionStatus, RoomType, UseCase } from "@/commons/services/RoomScheduleSettings";

import EnableSwitch from "@/pc/basics/EnableSwitch.vue";

/**
 * ### 公開情報（自分で設定）コンポーネント
 */
@Component({
  components: {
    EnableSwitch
  }
})
export default class CustomPublicSettingTable extends Vue {
  /** 親コンポーネントのバリデーターをInjectionする */
  @Inject("$validator") $validator!: Validator;

  /** 公開情報（自分で設定） */
  @Prop({ required: true })
  value!: Array<CustomPublicSetting>;
  /** ローディング */
  @Prop({ required: true })
  isLoading!: boolean;

  /** 列名 */
  fields: Array<CustomPublicSettingTableField> = [];
  /** 空床情報の選択項目 */
  receptionStatusOptions: Array<{ value: any; text: string }> = [
    { value: ReceptionStatus.CANT, text: "×" },
    { value: ReceptionStatus.CAN, text: "○" },
    { value: ReceptionStatus.TEL, text: "電話" }
  ];
  /** 空床数の選択項目 */
  receptionCountOptions: Array<{ value: any; text: string }> = [
    { value: ReceptionCount.NOT_PUBLIC, text: "非公開" },
    { value: ReceptionCount.BED_1, text: "1床" },
    { value: ReceptionCount.BED_2, text: "2床" },
    { value: ReceptionCount.BED_3, text: "3床" },
    { value: ReceptionCount.BED_4, text: "4床" },
    { value: ReceptionCount.BED_5, text: "5床" },
    { value: ReceptionCount.BED_6, text: "6床" },
    { value: ReceptionCount.BED_7, text: "7床" },
    { value: ReceptionCount.BED_8, text: "8床" },
    { value: ReceptionCount.BED_9, text: "9床" },
    { value: ReceptionCount.BED_OVER_10, text: "10床以上" }
  ];

  get _value() {
    return this.value;
  }

  /**
   * vue lifecycle mounted
   *
   * 画面表示情報の作成を行う
   */
  public async mounted(): Promise<void> {
    // 画面初期表示時に作成して変更しないものを初期化する
    const fields: Array<CustomPublicSettingTableField> = this.createFields();
    this.fields = fields;
    return;
  }

  /**
   * ### 利用者の列名を作成する
   */
  public createFields(): Array<CustomPublicSettingTableField> {
    const fields: Array<CustomPublicSettingTableField> = [];
    fields.push({ key: "useCase", label: "用途", sortable: false, sortDirection: "desc", class: "use-case" });
    fields.push({ key: "roomType", label: "部屋種別", sortable: false, sortDirection: "desc", class: "room-type" });
    fields.push({ key: "receptionStatus", label: "空床情報", sortable: false, sortDirection: "desc", class: "reception-status" });
    fields.push({ key: "receptionCount", label: "空床数", sortable: false, sortDirection: "desc", class: "reception-count" });
    fields.push({ key: "use", label: "使用有無", sortable: false, sortDirection: "desc", class: "use text-center" });
    return fields;
  }

  public convertUseCaseName(item: CustomPublicSetting): string {
    switch (item.useCase) {
      case UseCase.LONG_STAY: {
        return "長期入所";
      }
      case UseCase.SHORT_STAY: {
        return "ショートステイ";
      }
      case UseCase.LONG_AND_SHORT_STAY: {
        return "長期・短期併用";
      }
      default: {
        return "";
      }
    }
  }

  public convertRoomTypeName(item: CustomPublicSetting): string {
    switch (item.roomType) {
      case RoomType.SINGLE: {
        return "個室";
      }
      case RoomType.MULTI_BED: {
        return "多床室";
      }
      case RoomType.MULTI_BED_MEN: {
        return "多床室（男性）";
      }
      case RoomType.MULTI_BED_WOMEN: {
        return "多床室（女性）";
      }
      case RoomType.MEN: {
        return "男性";
      }
      case RoomType.WOMEN: {
        return "女性";
      }
      default: {
        return "";
      }
    }
  }

  public disableReceptionStatus(item: CustomPublicSetting): boolean {
    return !item.use;
  }

  public disableReceptionCount(item: CustomPublicSetting): boolean {
    return !item.use || (item.receptionStatus == ReceptionStatus.CAN ? false : true);
  }
}

/** テーブルの列名の情報 */
export interface CustomPublicSettingTableField {
  key: string;
  label: string;
  sortable: boolean;
  sortDirection: string;
  class?: string;
}
</script>
